import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Loader2, Search, Check, X, Eye, Car, Trash2 } from 'lucide-react'
import { useAdminListings, useApproveListing, useRejectListing, useDeleteListing } from '../../../hooks/use-admin-cars'
import { DeleteConfirmDialog } from '../components/delete-confirm-dialog'
import type { CarListing } from '@/modules/cars/services/cars.service'

export function ListingsModerationPage() {
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL')
  const [search, setSearch] = useState('')

  const statusParam = selectedStatus === 'ALL' ? undefined : selectedStatus
  const { data: listings, isLoading } = useAdminListings(statusParam)

  const approveListing = useApproveListing()
  const rejectListing = useRejectListing()
  const deleteListing = useDeleteListing()

  const [inspectListing, setInspectListing] = useState<CarListing | null>(null)
  const [rejectModalListing, setRejectModalListing] = useState<CarListing | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CarListing | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const filteredListings = (listings || []).filter((item: any) => {
    if (search && !item.title.toLowerCase().includes(search.toLowerCase()) && !item.brand?.name.toLowerCase().includes(search.toLowerCase())) {
      return false
    }
    return true
  })

  const handleApprove = async (id: number) => {
    await approveListing.mutateAsync(id)
    if (inspectListing?.id === id) setInspectListing(null)
  }

  const handleRejectSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!rejectModalListing) return
    await rejectListing.mutateAsync({
      id: rejectModalListing.id,
      reason: rejectReason || 'Listing rejected by administrator',
    })
    setRejectModalListing(null)
    setRejectReason('')
    if (inspectListing?.id === rejectModalListing.id) setInspectListing(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Car Listings Moderation Queue</h1>
          <p className="text-sm text-muted-foreground">Review, approve, or reject seller and dealership vehicle submissions.</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative flex-1 w-full max-w-md">
          <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
          <Input placeholder="Search by vehicle title or brand..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="w-full sm:w-56">
          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
            <option value="ALL">All Statuses</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="PUBLISHED">Published</option>
            <option value="REJECTED">Rejected</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading vehicle moderation queue...</p>
        </div>
      ) : (
        <Card className="overflow-hidden border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-accent/40 text-muted-foreground border-b border-border uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-4">Vehicle</th>
                <th className="p-4">Seller / Merchant</th>
                <th className="p-4">Price</th>
                <th className="p-4">Year &amp; Mileage</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredListings.map((car: any) => (
                <tr key={car.id} className="hover:bg-accent/20 transition-colors">
                  <td className="p-4 font-bold">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg overflow-hidden border border-border shrink-0 bg-accent/20">
                        {Array.isArray(car.images) && car.images[0] ? (
                          <img src={typeof car.images[0] === 'string' ? car.images[0] : car.images[0].url} alt="" className="size-full object-cover" />
                        ) : (
                          <Car className="size-5 m-2.5 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="line-clamp-1">{car.title}</div>
                        <div className="text-xs font-normal text-muted-foreground">{car.brand?.name} • {car.model?.name}</div>
                      </div>
                    </div>
                  </td>

                  <td className="p-4">
                    <div className="font-semibold text-xs">{car.user ? `${car.user.firstName} ${car.user.lastName}` : 'Merchant'}</div>
                    <Badge variant="outline" className="text-[10px] uppercase font-extrabold">{car.user?.role || 'SELLER'}</Badge>
                  </td>

                  <td className="p-4 font-bold text-foreground">
                    ₹{Number(car.askingPrice).toLocaleString('en-IN')}
                  </td>

                  <td className="p-4 text-xs">
                    <div className="font-semibold">{car.manufactureYear}</div>
                    <div className="text-muted-foreground">{car.mileageKm?.toLocaleString('en-IN')} km</div>
                  </td>

                  <td className="p-4">
                    <Badge className={
                      car.status === 'PUBLISHED'
                        ? 'bg-emerald-500/20 text-emerald-500'
                        : car.status === 'PENDING_REVIEW'
                        ? 'bg-amber-500/20 text-amber-500'
                        : 'bg-rose-500/20 text-rose-500'
                    }>
                      {car.status}
                    </Badge>
                  </td>

                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setInspectListing(car)} title="Inspect Listing">
                        <Eye className="size-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget(car)}
                        className="size-8 p-0 text-rose-500 hover:text-rose-600 hover:bg-rose-500/10"
                        title="Delete Listing"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                      {car.status === 'PENDING_REVIEW' && (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white size-8 p-0" onClick={() => handleApprove(car.id)} title="Approve">
                            <Check className="size-4" />
                          </Button>
                          <Button size="sm" variant="destructive" className="size-8 p-0" onClick={() => setRejectModalListing(car)} title="Reject">
                            <X className="size-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Inspect Drawer Dialog */}
      {inspectListing && (
        <Dialog open={!!inspectListing} onOpenChange={() => setInspectListing(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 rounded-2xl border border-slate-200 dark:border-border">
            <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-border/60 bg-slate-50/50 dark:bg-card/50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pr-6">
                <div>
                  <DialogTitle className="font-display text-2xl font-bold tracking-tight text-foreground">
                    {inspectListing.title}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {[inspectListing.brand?.name, inspectListing.model?.name, inspectListing.generation?.name, inspectListing.variant?.name].filter(Boolean).join(' • ')}
                  </p>
                </div>
                <Badge className={`text-xs px-3 py-1 font-bold ${
                  inspectListing.status === 'PUBLISHED'
                    ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30'
                    : inspectListing.status === 'PENDING_REVIEW'
                    ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30'
                    : 'bg-rose-500/20 text-rose-500 border border-rose-500/30'
                }`}>
                  {inspectListing.status}
                </Badge>
              </div>
            </DialogHeader>

            <div className="p-6 space-y-6 text-sm">
              {/* Photo Gallery Grid */}
              <div className="space-y-3">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-muted-foreground flex items-center gap-2">
                  <Car className="size-4 text-primary" /> Vehicle Photo Gallery ({Array.isArray(inspectListing.images) ? inspectListing.images.length : 0})
                </h4>
                {Array.isArray(inspectListing.images) && inspectListing.images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {inspectListing.images.map((img: any, i: number) => {
                      const url = typeof img === 'string' ? img : img.url
                      return (
                        <div key={i} className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-border aspect-video bg-slate-100 dark:bg-accent/20 group">
                          <img src={url} alt={`Vehicle photo ${i + 1}`} className="size-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          {i === 0 && (
                            <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-bold">Primary</Badge>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="p-6 rounded-xl border border-dashed text-center text-xs text-muted-foreground bg-accent/10">
                    No photos attached for this listing.
                  </div>
                )}
              </div>

              {/* Merchant / Seller Info Card */}
              <Card className="p-4 bg-slate-50/80 dark:bg-accent/20 border border-slate-200 dark:border-border rounded-xl space-y-3">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-muted-foreground flex items-center gap-2">
                  <Eye className="size-4 text-primary" /> Seller &amp; Merchant Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground block text-[10px] uppercase font-bold">Merchant Name</span>
                    <span className="font-bold text-foreground text-sm">
                      {inspectListing.user ? `${inspectListing.user.firstName} ${inspectListing.user.lastName}` : 'Merchant'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[10px] uppercase font-bold">Contact Email</span>
                    <span className="font-semibold text-foreground flex items-center gap-1.5 mt-0.5">
                      {inspectListing.user?.email || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[10px] uppercase font-bold">Account Role &amp; Phone</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="font-extrabold text-[10px]">
                        {inspectListing.user?.role || 'SELLER'}
                      </Badge>
                      <span className="font-semibold text-foreground">{inspectListing.user?.phone || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Complete Specs Grid */}
              <div className="space-y-3">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-muted-foreground">
                  Vehicle Specifications &amp; Overview
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card className="p-3 bg-white dark:bg-card border border-slate-200 dark:border-border shadow-2xs">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold">Asking Price</div>
                    <div className="font-display font-extrabold text-base text-primary">₹{Number(inspectListing.askingPrice).toLocaleString('en-IN')}</div>
                    <div className="text-[10px] font-semibold text-slate-500">{inspectListing.negotiable ? 'Negotiable' : 'Fixed Price'}</div>
                  </Card>
                  <Card className="p-3 bg-white dark:bg-card border border-slate-200 dark:border-border shadow-2xs">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold">Manufacture Year</div>
                    <div className="font-bold text-sm text-foreground">{inspectListing.manufactureYear}</div>
                    <div className="text-[10px] font-semibold text-slate-500">Model Year</div>
                  </Card>
                  <Card className="p-3 bg-white dark:bg-card border border-slate-200 dark:border-border shadow-2xs">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold">Kilometers Driven</div>
                    <div className="font-bold text-sm text-foreground">{inspectListing.mileageKm?.toLocaleString('en-IN')} km</div>
                    <div className="text-[10px] font-semibold text-slate-500">Odometer</div>
                  </Card>
                  <Card className="p-3 bg-white dark:bg-card border border-slate-200 dark:border-border shadow-2xs">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold">Fuel &amp; Transmission</div>
                    <div className="font-bold text-sm text-foreground">{inspectListing.fuelType}</div>
                    <div className="text-[10px] font-semibold text-slate-500">{inspectListing.transmission}</div>
                  </Card>
                  <Card className="p-3 bg-white dark:bg-card border border-slate-200 dark:border-border shadow-2xs">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold">Ownership History</div>
                    <div className="font-bold text-sm text-foreground">{inspectListing.ownership || '1st Owner'}</div>
                  </Card>
                  <Card className="p-3 bg-white dark:bg-card border border-slate-200 dark:border-border shadow-2xs">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold">Condition</div>
                    <div className="font-bold text-sm text-foreground">{inspectListing.condition || 'USED'}</div>
                  </Card>
                  <Card className="p-3 bg-white dark:bg-card border border-slate-200 dark:border-border shadow-2xs">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold">Drive Type &amp; Color</div>
                    <div className="font-bold text-sm text-foreground">{inspectListing.driveType || 'FWD'}</div>
                    <div className="text-[10px] font-semibold text-slate-500">{inspectListing.exteriorColor || 'N/A'}</div>
                  </Card>
                  <Card className="p-3 bg-white dark:bg-card border border-slate-200 dark:border-border shadow-2xs">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold">Location</div>
                    <div className="font-bold text-sm text-foreground">{inspectListing.locationCity}, {inspectListing.locationState}</div>
                  </Card>
                </div>
              </div>

              {/* Features & Installed Options */}
              {Array.isArray(inspectListing.features) && inspectListing.features.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-muted-foreground">
                    Installed Features &amp; Equipment ({inspectListing.features.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {inspectListing.features.map((feat: string, idx: number) => (
                      <Badge key={idx} variant="secondary" className="text-xs font-semibold px-2.5 py-1 bg-slate-100 dark:bg-accent text-slate-700 dark:text-foreground">
                        <Check className="size-3 text-emerald-500 mr-1" /> {feat}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {inspectListing.description && (
                <div className="space-y-1.5">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-muted-foreground">Vehicle Description &amp; Notes</h4>
                  <div className="p-3.5 rounded-xl border border-slate-200 dark:border-border bg-slate-50/50 dark:bg-background/50 text-xs text-foreground whitespace-pre-line leading-relaxed">
                    {inspectListing.description}
                  </div>
                </div>
              )}

              {/* Rejection Reason (If previously rejected) */}
              {inspectListing.rejectionReason && (
                <div className="p-3.5 rounded-xl border border-rose-500/30 bg-rose-500/10 space-y-1">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-rose-500">Moderator Rejection Note</h4>
                  <p className="text-xs font-semibold text-foreground">{inspectListing.rejectionReason}</p>
                </div>
              )}

              {/* Moderation Actions Footer */}
              {inspectListing.status === 'PENDING_REVIEW' && (
                <div className="pt-4 border-t border-slate-200 dark:border-border flex flex-col sm:flex-row items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    Action required: Review all specs &amp; images before approving for public marketplace indexing.
                  </p>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <Button variant="outline" className="font-bold text-destructive hover:bg-rose-50 dark:hover:bg-rose-950/20 border-rose-200 shrink-0" onClick={() => setRejectModalListing(inspectListing)}>
                      <X className="size-4 mr-1.5" /> Reject Listing
                    </Button>
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2 shadow-md shrink-0" onClick={() => handleApprove(inspectListing.id)} disabled={approveListing.isPending}>
                      {approveListing.isPending ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />} Approve &amp; Publish
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reject Modal */}
      {rejectModalListing && (
        <Dialog open={!!rejectModalListing} onOpenChange={() => setRejectModalListing(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Listing #{rejectModalListing.id}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRejectSubmit} className="space-y-4 pt-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason for Rejection</label>
                <Input placeholder="e.g. Incomplete specifications or invalid pricing" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} required />
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setRejectModalListing(null)}>Cancel</Button>
                <Button type="submit" variant="destructive" disabled={rejectListing.isPending}>
                  {rejectListing.isPending ? <Loader2 className="size-4 animate-spin" /> : 'Reject Submission'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Listing Confirmation Dialog */}
      {deleteTarget && (
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteListing.mutateAsync(deleteTarget.id)
            if (inspectListing?.id === deleteTarget.id) setInspectListing(null)
          }}
          title={`Delete Listing #${deleteTarget.id}`}
          entityName={deleteTarget.title}
          entityType="Listing"
          isIndependent={true}
          blockingReason={`Cannot delete listing #${deleteTarget.id}.`}
        />
      )}
    </div>
  )
}
